import { ui } from '@procuros/datachecks';
import { TFunction } from 'i18next';

import { PartnerProductDTO } from 'services/repositories/masterdata/masterdata';

/**
 * Mirrors the generated `UnitOfMeasure` union (`schema-portal.d.ts`) as a runtime list for the
 * dropdown. Typing each entry against `PartnerProductDTO['unit_of_measure']` catches a code that
 * no longer exists in the schema; it cannot catch one the schema gained and this list is missing.
 */
const UNIT_OF_MEASURE_CODES: Array<PartnerProductDTO['unit_of_measure']> = [
  'EA',
  'GRM',
  'HUR',
  'KGM',
  'LTR',
  'MLT',
  'MTK',
  'MTR',
  'PCK',
  'SET',
  'CS',
  'CA',
  'DAY',
  'WEE',
  'MON',
  'CT',
  'CQ',
  'CU',
  'BJ',
  'BO',
  'MTQ',
  'PF',
  'BG',
  'RO',
  'TNE',
  'BE',
  'CMT',
  'KMT',
  'PR',
  'TU',
  'COL',
];

/**
 * The `DataTable` column config for Product Masterdata's editable fields — everything except the
 * `Log` column, which is a display-only column injected separately (it has no spec field to
 * describe it). All fields are currently editable (`is_readonly: false`); the PATCH endpoint has
 * not confirmed whether `gtin`/`name`/`articleNumber` (nested identifiers/attributes) are
 * patchable, so that constraint may need revisiting once the schema lands.
 */
type ProductMasterdataColumnConfig = ui.Field & { extraFields?: { width?: number } };

/**
 * Fixed pixel widths for the free-text identifier columns. Without them every column gets the same
 * share of the table, so long product names are cut off (CORE-12465). Edit-mode cells are single-line
 * inputs and can't wrap, so the width is what keeps the values readable.
 */
const IDENTIFIER_COLUMN_WIDTH = 180;
const NAME_COLUMN_WIDTH = 320;

export const buildProductMasterdataColumnsConfig = (t: TFunction): Array<ProductMasterdataColumnConfig> => [
  {
    type: ui.FieldTypes.text,
    path: 'gtin',
    label: t('settings:productMasterdata.table.headers.gtin'),
    description: t('settings:productMasterdata.table.headers.gtinDescription'),
    modality: 'OPTIONAL',
    is_hidden: false,
    is_readonly: false,
    is_virtual: false,
    sort_order: 1,
    extraFields: { width: IDENTIFIER_COLUMN_WIDTH },
  },
  {
    type: ui.FieldTypes.text,
    path: 'name',
    label: t('settings:productMasterdata.table.headers.name'),
    description: t('settings:productMasterdata.table.headers.nameDescription'),
    modality: 'OPTIONAL',
    is_hidden: false,
    is_readonly: false,
    is_virtual: false,
    sort_order: 2,
    extraFields: { width: NAME_COLUMN_WIDTH },
  },
  {
    type: ui.FieldTypes.text,
    path: 'articleNumber',
    label: t('settings:productMasterdata.table.headers.articleNumber'),
    description: t('settings:productMasterdata.table.headers.articleNumberDescription'),
    modality: 'OPTIONAL',
    is_hidden: false,
    is_readonly: false,
    is_virtual: false,
    sort_order: 3,
    extraFields: { width: IDENTIFIER_COLUMN_WIDTH },
  },
  {
    type: ui.FieldTypes.select,
    path: 'unit_of_measure',
    label: t('settings:productMasterdata.table.headers.unitOfMeasure'),
    description: t('settings:productMasterdata.table.headers.unitOfMeasureDescription'),
    type_config: {
      options: UNIT_OF_MEASURE_CODES.map((code) => ({
        value: code,
        label: t(`common:unitOfMeasure.${code}`, { defaultValue: code }),
      })),
    },
    modality: 'OPTIONAL',
    is_hidden: false,
    is_readonly: false,
    is_virtual: false,
    sort_order: 4,
  },
  {
    type: ui.FieldTypes.boolean,
    path: 'is_orderable',
    label: t('settings:productMasterdata.table.headers.orderable'),
    description: t('settings:productMasterdata.table.headers.orderableDescription'),
    modality: 'OPTIONAL',
    is_hidden: false,
    is_readonly: false,
    is_virtual: false,
    sort_order: 5,
  },
  {
    type: ui.FieldTypes.number,
    path: 'unit_price',
    label: t('settings:productMasterdata.table.headers.unitPrice'),
    description: t('settings:productMasterdata.table.headers.unitPriceDescription'),
    modality: 'OPTIONAL',
    is_hidden: false,
    is_readonly: false,
    is_virtual: false,
    sort_order: 6,
  },
  {
    type: ui.FieldTypes.number,
    path: 'tax_rate',
    label: t('settings:productMasterdata.table.headers.taxRate'),
    description: t('settings:productMasterdata.table.headers.taxRateDescription'),
    type_config: { suffix: '%' },
    modality: 'OPTIONAL',
    is_hidden: false,
    is_readonly: false,
    is_virtual: false,
    sort_order: 7,
  },
  {
    // `text`, not `select`: no currency list exists in this codebase yet (`support/helpers/currency/currency.ts`
    // only has `isValidCurrency`, not an enumerable list). Validation is deferred to the PATCH follow-up.
    type: ui.FieldTypes.text,
    path: 'currency',
    label: t('settings:productMasterdata.table.headers.currency'),
    description: t('settings:productMasterdata.table.headers.currencyDescription'),
    modality: 'OPTIONAL',
    is_hidden: false,
    is_readonly: false,
    is_virtual: false,
    sort_order: 8,
  },
];

/**
 * i18n keys
 * t('settings:productMasterdata.table.headers.gtin')
 * t('settings:productMasterdata.table.headers.gtinDescription')
 * t('settings:productMasterdata.table.headers.name')
 * t('settings:productMasterdata.table.headers.nameDescription')
 * t('settings:productMasterdata.table.headers.articleNumber')
 * t('settings:productMasterdata.table.headers.articleNumberDescription')
 * t('settings:productMasterdata.table.headers.unitOfMeasure')
 * t('settings:productMasterdata.table.headers.unitOfMeasureDescription')
 * t('settings:productMasterdata.table.headers.orderable')
 * t('settings:productMasterdata.table.headers.orderableDescription')
 * t('settings:productMasterdata.table.headers.unitPrice')
 * t('settings:productMasterdata.table.headers.unitPriceDescription')
 * t('settings:productMasterdata.table.headers.taxRate')
 * t('settings:productMasterdata.table.headers.taxRateDescription')
 * t('settings:productMasterdata.table.headers.currency')
 * t('settings:productMasterdata.table.headers.currencyDescription')
 * t('common:unitOfMeasure.BE')
 * t('common:unitOfMeasure.BG')
 * t('common:unitOfMeasure.BJ')
 * t('common:unitOfMeasure.BO')
 * t('common:unitOfMeasure.CA')
 * t('common:unitOfMeasure.CMT')
 * t('common:unitOfMeasure.COL')
 * t('common:unitOfMeasure.CQ')
 * t('common:unitOfMeasure.CS')
 * t('common:unitOfMeasure.CT')
 * t('common:unitOfMeasure.CU')
 * t('common:unitOfMeasure.DAY')
 * t('common:unitOfMeasure.EA')
 * t('common:unitOfMeasure.GRM')
 * t('common:unitOfMeasure.HUR')
 * t('common:unitOfMeasure.KGM')
 * t('common:unitOfMeasure.KMT')
 * t('common:unitOfMeasure.LTR')
 * t('common:unitOfMeasure.MLT')
 * t('common:unitOfMeasure.MON')
 * t('common:unitOfMeasure.MTK')
 * t('common:unitOfMeasure.MTQ')
 * t('common:unitOfMeasure.MTR')
 * t('common:unitOfMeasure.PCK')
 * t('common:unitOfMeasure.PF')
 * t('common:unitOfMeasure.PR')
 * t('common:unitOfMeasure.RO')
 * t('common:unitOfMeasure.SET')
 * t('common:unitOfMeasure.TNE')
 * t('common:unitOfMeasure.TU')
 * t('common:unitOfMeasure.WEE')
 */
